import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useColor4 } from '../hooks/useColor4';
import { usePropertyCountry } from '../utils/propertyCountry';

// ============================================================================
// Static slot group definitions for each program card
// ============================================================================
// Image slot groups default to stock photos (Unsplash) chosen to evoke each
// program's theme. Operators can swap in licensed or property-owned assets
// via the editor's media picker.

const card1ImageDef: SlotGroupDefinition = {
  label: 'Referral Program Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Diverse group of friends spending time together' },
  },
};

const card1CtaDef: SlotGroupDefinition = {
  label: 'Referral Program CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Refer a Friend' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Preferred Employer Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multi-ethnic team collaborating in a bright office' },
  },
};

const card2CtaDef: SlotGroupDefinition = {
  label: 'Preferred Employer CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Check Eligibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Resident Ambassador Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&crop=faces&w=1200&h=900&q=80',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Resident smiling and sharing her story' },
  },
};

const card3CtaDef: SlotGroupDefinition = {
  label: 'Resident Ambassador CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Become an Ambassador' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function ArrowIcon() {
  return (
    <svg className="relative z-10 w-4 h-4 shrink-0 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
    </svg>
  );
}

// ============================================================================
// Component
// ============================================================================

export function RewardsPrograms() {
  const card1Title = useContentToken('rewardsprograms.card1_title');
  const card2Title = useContentToken('rewardsprograms.card2_title');
  const card3Title = useContentToken('rewardsprograms.card3_title');

  const card1Eyebrow = useContentToken('rewardsprograms.card1_eyebrow');
  const card2Eyebrow = useContentToken('rewardsprograms.card2_eyebrow');
  const card3Eyebrow = useContentToken('rewardsprograms.card3_eyebrow');

  const card1Image = useSlotGroup('rewardsprograms.card1_image', card1ImageDef);
  const card2Image = useSlotGroup('rewardsprograms.card2_image', card2ImageDef);
  const card3Image = useSlotGroup('rewardsprograms.card3_image', card3ImageDef);

  const card1Cta = useSlotGroup('rewardsprograms.card1_cta', card1CtaDef);
  const card2Cta = useSlotGroup('rewardsprograms.card2_cta', card2CtaDef);
  const card3Cta = useSlotGroup('rewardsprograms.card3_cta', card3CtaDef);
  const color4 = useColor4();
  const isCanadian = usePropertyCountry() === 'ca';

  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });
  const disclaimerRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pt-4 pb-6 md:pt-6 md:pb-10 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Card 1 — Resident Referral Program */}
          <article data-reveal className="reveal-fade-up rp-card bg-white border border-[var(--vibe-color-border)] overflow-hidden flex flex-col">
            <EditableSlotGroup groupPath="rewardsprograms.card1_image" definition={card1ImageDef} className="rp-image-banner">
              {card1Image.image ? (
                <VibesitesImage
                  url={card1Image.image}
                  alt={card1Image.alt || 'Resident Referral Program'}
                  sizes="(min-width: 1024px) 400px, (min-width: 768px) 40vw, 100vw"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-[color-mix(in_srgb,var(--vibe-color-primary)_20%,white)] to-[color-mix(in_srgb,var(--vibe-color-primary)_8%,white)]" />
              )}
            </EditableSlotGroup>
            <div className="rp-card-body p-6 sm:p-8 flex flex-col flex-grow">
              <EditableToken
                path="rewardsprograms.card1_eyebrow"
                as="span"
                className="font-body text-xs sm:text-sm font-semibold tracking-widest uppercase text-[var(--vibe-color-primary)] mb-2"
              >
                {card1Eyebrow}
              </EditableToken>
              <EditableToken
                path="rewardsprograms.card1_title"
                as="h3"
                className="font-heading text-xl sm:text-2xl font-semibold text-[var(--vibe-color-text)] mb-3 leading-tight"
              >
                {card1Title}
              </EditableToken>
              <RichTextContent
                path="rewardsprograms.card1_description"
                defaultValue="<p>Earn rent credits when you refer friends who become part of our community. It's an easy way to help someone find their next apartment while lowering your own housing costs.</p>"
                as="div"
                className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed flex-grow mb-6 [&_p]:mb-2 [&_p:last-child]:mb-0"
              />
              <EditableSlotGroup groupPath="rewardsprograms.card1_cta" definition={card1CtaDef}>
                <a
                  href={card1Cta.url || '/contact-us'}
                  target={card1Cta._target || '_self'}
                  rel={card1Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  style={color4?.primaryOverride}
                  className="rp-cta group/cta relative inline-flex items-center justify-center gap-2.5 px-8 py-4 text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
                >
                  <span className="relative z-10">{card1Cta.label || 'Refer a Friend'}</span>
                  <ArrowIcon />
                </a>
              </EditableSlotGroup>
            </div>
          </article>

          {/* Card 2 — Preferred Employer Program */}
          <article data-reveal className="reveal-fade-up rp-card bg-white border border-[var(--vibe-color-border)] overflow-hidden flex flex-col">
            <EditableSlotGroup groupPath="rewardsprograms.card2_image" definition={card2ImageDef} className="rp-image-banner">
              {card2Image.image ? (
                <VibesitesImage
                  url={card2Image.image}
                  alt={card2Image.alt || 'Preferred Employer Program'}
                  sizes="(min-width: 1024px) 400px, (min-width: 768px) 40vw, 100vw"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-[color-mix(in_srgb,var(--vibe-color-primary)_20%,white)] to-[color-mix(in_srgb,var(--vibe-color-primary)_8%,white)]" />
              )}
            </EditableSlotGroup>
            <div className="rp-card-body p-6 sm:p-8 flex flex-col flex-grow">
              <EditableToken
                path="rewardsprograms.card2_eyebrow"
                as="span"
                className="font-body text-xs sm:text-sm font-semibold tracking-widest uppercase text-[var(--vibe-color-primary)] mb-2"
              >
                {card2Eyebrow}
              </EditableToken>
              <EditableToken
                path="rewardsprograms.card2_title"
                as="h3"
                className="font-heading text-xl sm:text-2xl font-semibold text-[var(--vibe-color-text)] mb-3 leading-tight"
              >
                {card2Title}
              </EditableToken>
              {isCanadian ? (
                <RichTextContent
                  path="rewardsprograms.card2_description_canada"
                  defaultValue="<p>Eligible employees of partner companies receive rent discounts on top of current leasing specials. It's our way of supporting the local employers who power our neighbourhood.</p>"
                  as="div"
                  className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed flex-grow mb-6 [&_p]:mb-2 [&_p:last-child]:mb-0"
                />
              ) : (
                <RichTextContent
                  path="rewardsprograms.card2_description"
                  defaultValue="<p>Eligible employees of partner companies receive rent discounts on top of current leasing specials. It's our way of supporting the local employers who power our neighborhood.</p>"
                  as="div"
                  className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed flex-grow mb-6 [&_p]:mb-2 [&_p:last-child]:mb-0"
                />
              )}
              <EditableSlotGroup groupPath="rewardsprograms.card2_cta" definition={card2CtaDef}>
                <a
                  href={card2Cta.url || '/contact-us'}
                  target={card2Cta._target || '_self'}
                  rel={card2Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  style={color4?.primaryOverride}
                  className="rp-cta group/cta relative inline-flex items-center justify-center gap-2.5 px-8 py-4 text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
                >
                  <span className="relative z-10">{card2Cta.label || 'Check Eligibility'}</span>
                  <ArrowIcon />
                </a>
              </EditableSlotGroup>
            </div>
          </article>

          {/* Card 3 — Resident Ambassador Program */}
          <article data-reveal className="reveal-fade-up rp-card bg-white border border-[var(--vibe-color-border)] overflow-hidden flex flex-col">
            <EditableSlotGroup groupPath="rewardsprograms.card3_image" definition={card3ImageDef} className="rp-image-banner">
              {card3Image.image ? (
                <VibesitesImage
                  url={card3Image.image}
                  alt={card3Image.alt || 'Resident Ambassador Program'}
                  sizes="(min-width: 1024px) 400px, (min-width: 768px) 40vw, 100vw"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-[color-mix(in_srgb,var(--vibe-color-primary)_20%,white)] to-[color-mix(in_srgb,var(--vibe-color-primary)_8%,white)]" />
              )}
            </EditableSlotGroup>
            <div className="rp-card-body p-6 sm:p-8 flex flex-col flex-grow">
              <EditableToken
                path="rewardsprograms.card3_eyebrow"
                as="span"
                className="font-body text-xs sm:text-sm font-semibold tracking-widest uppercase text-[var(--vibe-color-primary)] mb-2"
              >
                {card3Eyebrow}
              </EditableToken>
              <EditableToken
                path="rewardsprograms.card3_title"
                as="h3"
                className="font-heading text-xl sm:text-2xl font-semibold text-[var(--vibe-color-text)] mb-3 leading-tight"
              >
                {card3Title}
              </EditableToken>
              <RichTextContent
                path="rewardsprograms.card3_description"
                defaultValue="<p>Get paid for answering questions about community life. Share your experience with prospective renters and earn compensation along the way — your story matters.</p>"
                as="div"
                className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed flex-grow mb-6 [&_p]:mb-2 [&_p:last-child]:mb-0"
              />
              <EditableSlotGroup groupPath="rewardsprograms.card3_cta" definition={card3CtaDef}>
                <a
                  href={card3Cta.url || '/contact-us'}
                  target={card3Cta._target || '_self'}
                  rel={card3Cta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  style={color4?.primaryOverride}
                  className="rp-cta group/cta relative inline-flex items-center justify-center gap-2.5 px-8 py-4 text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
                >
                  <span className="relative z-10">{card3Cta.label || 'Become an Ambassador'}</span>
                  <ArrowIcon />
                </a>
              </EditableSlotGroup>
            </div>
          </article>
        </div>

        <div ref={disclaimerRef} className="reveal-fade-up mt-16 max-w-4xl mx-auto">
          <RichTextContent
            path="rewardsprograms.disclaimer"
            label="Disclaimer"
            defaultValue="<p>Rewards program availability, amounts, and eligibility are subject to change and may vary by community. Participation requires compliance with program terms and leasing office approval. Contact our team for current program details and to confirm eligibility.</p>"
            as="div"
            className="font-body text-xs sm:text-sm text-[var(--vibe-color-text-muted)]/80 italic leading-relaxed text-center [&_p]:m-0"
          />
        </div>
      </div>

      <style>{`
        .rp-card {
          border-radius: 4px;
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .rp-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 30px -10px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 50%, var(--vibe-color-border));
        }
        .rp-image-banner {
          width: 100%;
          aspect-ratio: 4 / 3;
          display: block;
          overflow: hidden;
          background: color-mix(in srgb, var(--vibe-color-primary) 7%, white);
          border-bottom: 1px solid var(--vibe-color-border);
        }
        .rp-cta {
          align-self: flex-start;
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .rp-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .rp-cta:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .rp-cta:hover::before {
          left: 100%;
        }
        .rp-cta:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        @media (min-width: 768px) and (max-width: 1023.98px) {
          .rp-card {
            flex-direction: row;
          }
          .rp-image-banner {
            position: relative;
            width: 40%;
            flex-shrink: 0;
            aspect-ratio: auto;
            min-height: 280px;
            border-bottom: 0;
            border-right: 1px solid var(--vibe-color-border);
          }
          .rp-image-banner img {
            position: absolute;
            inset: 0;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'RewardsPrograms',
  displayName: 'Rewards Programs',
  tokens: {
    'rewardsprograms.card1_eyebrow': {
      label: 'Referral Program Eyebrow',
      type: 'text',
      defaultValue: 'Resident Referral Program',
    },
    'rewardsprograms.card1_title': {
      label: 'Referral Program Title',
      type: 'text',
      defaultValue: 'Love Your Home? It Pays to Share It.',
    },
    'rewardsprograms.card1_description': {
      label: 'Referral Program Description',
      type: 'richtext',
      defaultValue:
        "<p>Earn rent credits when you refer friends who become part of our community. It's an easy way to help someone find their next apartment while lowering your own housing costs.</p>",
    },
    'rewardsprograms.card2_eyebrow': {
      label: 'Preferred Employer Eyebrow',
      type: 'text',
      defaultValue: 'Preferred Employer Program',
    },
    'rewardsprograms.card2_title': {
      label: 'Preferred Employer Title',
      type: 'text',
      defaultValue: 'Your Job Comes with Built-In Savings.',
    },
    'rewardsprograms.card2_description': {
      label: 'Preferred Employer Description',
      type: 'richtext',
      defaultValue:
        "<p>Eligible employees of partner companies receive rent discounts on top of current leasing specials. It's our way of supporting the local employers who power our neighborhood.</p>",
    },
    'rewardsprograms.card2_description_canada': {
      label: 'Preferred Employer Description (Canadian Properties)',
      type: 'richtext',
      defaultValue:
        "<p>Eligible employees of partner companies receive rent discounts on top of current leasing specials. It's our way of supporting the local employers who power our neighbourhood.</p>",
    },
    'rewardsprograms.card3_eyebrow': {
      label: 'Resident Ambassador Eyebrow',
      type: 'text',
      defaultValue: 'Resident Ambassador Program',
    },
    'rewardsprograms.card3_title': {
      label: 'Resident Ambassador Title',
      type: 'text',
      defaultValue: 'Turn Your Story Into Extra Cash.',
    },
    'rewardsprograms.card3_description': {
      label: 'Resident Ambassador Description',
      type: 'richtext',
      defaultValue:
        '<p>Get paid for answering questions about community life. Share your experience with prospective renters and earn compensation along the way — your story matters.</p>',
    },
    'rewardsprograms.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue:
        '<p>Rewards program availability, amounts, and eligibility are subject to change and may vary by community. Participation requires compliance with program terms and leasing office approval. Contact our team for current program details and to confirm eligibility.</p>',
    },
  },
  slotGroups: {
    'rewardsprograms.card1_image': card1ImageDef,
    'rewardsprograms.card1_cta': card1CtaDef,
    'rewardsprograms.card2_image': card2ImageDef,
    'rewardsprograms.card2_cta': card2CtaDef,
    'rewardsprograms.card3_image': card3ImageDef,
    'rewardsprograms.card3_cta': card3CtaDef,
  },
};

(RewardsPrograms as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default RewardsPrograms;
